import React, { useEffect, useState } from 'react';
import { foundryPosts, foundryPostUrl } from './foundry-instagram-data';
import PhoneStack from './PhoneStack';

const SITE = 'https://thefoundrybrief.com';
const IG_URL = 'https://www.instagram.com/thefoundrybrief';

const NAVY = '#1e3a5f';

/* ------------------------------------------------------------------ DATA --- */

const STATS = [
  ['557', 'Articles'],
  ['118', 'Founders'],
  ['15', 'Topics'],
  ['696', 'Pages'],
  ['1.53M', 'Reel views']
];

const TOPICS = [
  ['Leadership', 145], ['Startup Growth', 85], ['Investing', 73],
  ['Mindset & Career', 62], ['Product & Building', 54], ['Strategy', 29],
  ['Founder Stories', 28], ['AI & Technology', 25]
];

const FOUNDERS = [
  'Peter Thiel', 'Sam Altman', 'Elon Musk', 'Marc Andreessen', 'Jeff Bezos',
  'Naval Ravikant', 'Mark Zuckerberg', 'Jensen Huang', 'Patrick Collison',
  'Steve Jobs', 'Palmer Luckey', 'Reed Hastings', 'Ben Horowitz', 'Keith Rabois',
  'Aaron Levie', 'Paul Graham', 'Brian Chesky', 'Vinod Khosla'
];

const ARTICLES = [
  {
    id: 'sa-141', person: 'Steve Jobs', topic: 'Leadership', read: '1 min',
    headline: 'Steve Jobs Got Fired From His Own Company. He Called It the Best Thing That Happened to Him.',
    quote: 'We all need to be on guard against arrogance, which knocks at the door whenever you’re successful.',
    slug: 'sa-141-steve-jobs-got-fired-from-his-own-company-he-called-it-the-best-thing-that'
  },
  {
    id: 'sa-125', person: 'Marc Andreessen', topic: 'Leadership', read: '1 min',
    headline: 'Marc Andreessen: There Are No Silver Bullets. Only Lead Bullets.',
    quote: 'There are no silver bullets, there are only lead bullets.',
    slug: 'sa-125-marc-andreessen-there-are-no-silver-bullets-only-lead-bullets'
  },
  {
    id: 'sa-082', person: 'Tobi Lutke', topic: 'Leadership', read: '1 min',
    headline: 'Shopify Doesn’t Use KPIs or OKRs. Tobi Lutke Explains Why.',
    quote: 'The moment a metric becomes a goal, it’s no longer a useful metric.',
    slug: 'sa-082-shopify-doesnt-use-kpis-or-okrs-tobi-lutke-explains-why'
  },
  {
    id: 'sa-050', person: 'Tony Xu', topic: 'Investing', read: '2 min',
    headline: 'DoorDash Almost Died After a Football Game. Then Tony Xu Baked Cookies at 5 AM.',
    quote: 'We took maybe 10 seconds to make the decision to refund everybody.',
    slug: 'sa-050-doordash-almost-died-after-a-football-game-then-tony-xu-baked-cookies-at-5-am'
  },
  {
    id: 'sa-456', person: 'Tony Fadell', topic: 'Startup Growth', read: '1 min',
    headline: 'Tony Fadell: What Made Steve Jobs Different Was Not Vision. It Was Opinions.',
    quote: 'They’re trying to turn opinion-based decisions into data-driven decisions so they don’t lose their jobs.',
    slug: 'sa-456-tony-fadell-what-made-steve-jobs-different-was-not-vision-it-was-opinions'
  },
  {
    id: 'sa-247', person: 'Patrick Collison', topic: 'Mindset & Career', read: '1 min',
    headline: 'Patrick Collison: In Your 20s, Find a Place With High Standards.',
    quote: 'Figure out where you can learn the highest standards. Where are the highest standards embodied?',
    slug: 'sa-247-patrick-collison-in-your-20s-find-a-place-with-high-standards-that-changes'
  }
];

const READER = [
  'Two minutes, not two hours',
  'A page for every founder',
  'Search that finds the idea',
  'Save what you liked',
  'A brief in your inbox',
  'Reads right at night'
];

const PALETTE = [
  ['#06223d', 'Editorial navy', 'Masthead, newsletter band, links'],
  ['#b39e89', 'Warm sand', 'The one flourish — wordmark and labels'],
  ['#faf8f5', 'Paper', 'The reading surface, cream with no yellow'],
  ['#0a0f16', 'Night paper', 'Dark mode — library at night, never pure black']
];

const SCREENS = [
  ['/foundry-screens/home.webp', 'Front page'],
  ['/foundry-screens/article.webp', 'Article page'],
  ['/foundry-screens/founders.webp', 'Founders index'],
  ['/foundry-screens/topics.webp', 'Topics index']
];

const REELS = foundryPosts.slice(0, 18);

const fmt = (n) => {
  if (!n) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `${Math.round(n / 1000)}K`;
  return `${n}`;
};

/* ------------------------------------------------------------------- CSS --- */

const Styles = () => (
  <style>{`
    .fdy * { box-sizing: border-box; }

    .fdy-stats { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; }
    .fdy-stats .fdy-stat { flex: 0 0 108px; }
    .fdy-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: clamp(12px, 1.8vw, 18px); }
    .fdy-grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: clamp(12px, 1.8vw, 18px); }
    .fdy-reels { display: grid; grid-template-columns: repeat(6, 1fr); gap: clamp(7px, 1vw, 10px); }

    .fdy-card {
      border: 1px solid var(--border-color);
      border-radius: 10px;
      background: var(--bg-secondary);
      overflow: hidden;
      text-decoration: none;
      display: flex;
      flex-direction: column;
      transition: transform 0.2s ease, box-shadow 0.2s ease;
    }
    .fdy-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.10); }

    .fdy-h3 { font-size: 15px; font-weight: 600; color: var(--text-primary); line-height: 1.35; margin: 0; }
    .fdy-p  { font-size: 14px; line-height: 1.65; color: var(--text-secondary); margin: 0; }
    .fdy-label {
      font-size: 11px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase;
      color: var(--text-secondary); margin: 0;
    }
    .fdy-meta { font-size: 14px; font-weight: 500; color: var(--text-primary); margin: 0; line-height: 1.5; }

    .fdy-stat { text-align: center; padding: 9px 6px; border: 1px solid var(--border-color); border-radius: 8px; background: var(--bg-secondary); }
    .fdy-stat b { display: block; font-size: clamp(14px, 1.5vw, 17px); font-weight: 700; color: var(--text-primary); line-height: 1.15; }
    .fdy-stat span { display: block; font-size: 10px; color: var(--text-secondary); margin-top: 3px; }

    .fdy-chip {
      display: inline-block; border: 1px solid var(--border-color); border-radius: 999px;
      padding: 6px 12px; font-size: 13px; color: var(--text-primary); background: var(--bg-secondary);
    }

    .fdy-reel { position: relative; display: block; aspect-ratio: 9/16; border-radius: 8px; overflow: hidden; background: #0d2340; border: 1px solid var(--border-color); text-decoration: none; }
    .fdy-reel img { width: 100%; height: 100%; object-fit: cover; display: block; }
    .fdy-reel .m {
      position: absolute; left: 0; right: 0; bottom: 0; padding: 14px 7px 6px; color: #fff;
      font-size: 11px; font-weight: 700; text-align: center;
      background: linear-gradient(to top, rgba(0,0,0,0.85), transparent);
    }
    .fdy-reel .ph { position: absolute; inset: 0; padding: 10px 9px 30px; color: #f0e6d8; font-size: 11px; line-height: 1.35; overflow: hidden; }
    .fdy-reel .ph b { display: block; color: #d9c6ad; font-size: 11px; margin-bottom: 4px; }
    .fdy-reel .ph .t { display: -webkit-box; -webkit-line-clamp: 8; -webkit-box-orient: vertical; overflow: hidden; }

    /* ---------------- phones: smaller type, keep the columns ---------------- */
    @media (max-width: 760px) {
      .fdy-grid-3 { grid-template-columns: repeat(2, 1fr); gap: 10px; }
      .fdy-reels { grid-template-columns: repeat(3, 1fr); gap: 6px; }
      .fdy-p  { font-size: 12px; line-height: 1.55; }
      .fdy-h3 { font-size: 13px; }
      .fdy-label { font-size: 10px; }
      .fdy-chip { font-size: 11px; padding: 5px 9px; }
      .fdy-meta { font-size: 12px; }
      .fdy-btn { font-size: 12px !important; padding: 9px 14px !important; }
      .fdy-stats { flex-wrap: nowrap; gap: 5px; }
      .fdy-stats .fdy-stat { flex: 1 1 0; min-width: 0; padding: 7px 2px; border-radius: 7px; }
      .fdy-stat b { font-size: 13px; }
      .fdy-stat span { font-size: 8.5px; margin-top: 2px; white-space: nowrap; }
      .fdy-reel .m { font-size: 9px; padding: 12px 4px 4px; }
      .fdy-reel .ph { font-size: 9px; padding: 8px 7px 22px; }
      .fdy-reel .ph .t { -webkit-line-clamp: 6; }
      .fdy-reel .ph b { font-size: 9px; }
    }
    @media (max-width: 430px) {
      .fdy-stats { gap: 4px; }
      .fdy-stat b { font-size: 12px; }
      .fdy-stat span { font-size: 7.5px; letter-spacing: -0.01em; }
    }
    /* the narrowest phones still have to hold five on one line */
    @media (max-width: 360px) {
      .fdy-stats { gap: 3px; }
      .fdy-stats .fdy-stat { padding: 6px 1px; }
      .fdy-stat b { font-size: 10.5px; }
      .fdy-stat span { font-size: 6.5px; }
      .fdy-p { font-size: 11.5px; }
    }
  `}</style>
);

/* ------------------------------------------------------------------ HERO --- */

const Btn = ({ href, children, primary }) => {
  const [h, setH] = useState(false);
  return (
    <a
      href={href} target="_blank" rel="noopener noreferrer" className="fdy-btn"
      onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{
        background: primary ? (h ? '#2a4f80' : NAVY) : 'var(--bg-secondary)',
        color: primary ? '#fff' : 'var(--text-primary)',
        border: primary ? '1px solid transparent' : '1px solid var(--border-color)',
        padding: '11px 22px', borderRadius: '8px', textDecoration: 'none',
        fontWeight: 600, fontSize: '14px',
        transform: h ? 'translateY(-2px)' : 'none',
        transition: 'all 0.2s ease', whiteSpace: 'nowrap'
      }}
    >
      {children}
    </a>
  );
};

const Hero = () => (
  <div className="fdy" style={{ textAlign: 'center', paddingTop: 'clamp(8px, 2vw, 20px)' }}>
    <Styles />

    <img
      src="/foundry-tfb-mark.png"
      alt="The Foundry Brief"
      style={{ width: 'clamp(58px, 8vw, 76px)', height: 'auto', borderRadius: '12px', display: 'block', margin: '0 auto 20px' }}
    />

    <p className="fdy-label" style={{ marginBottom: '12px' }}>Business publication</p>

    <h1 style={{
      fontSize: 'clamp(1.9rem, 4.4vw, 2.7rem)', fontWeight: 700, letterSpacing: '-0.015em',
      color: 'var(--text-primary)', margin: '0 0 12px'
    }}>
      The Foundry Brief
    </h1>

    <p style={{
      fontSize: 'clamp(1rem, 1.8vw, 1.15rem)', color: 'var(--text-secondary)',
      lineHeight: 1.6, maxWidth: '620px', margin: '0 auto 26px'
    }}>
      A business publication.
    </p>

    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: 'clamp(26px, 4vw, 40px)' }}>
      <Btn href={SITE} primary>Visit the site</Btn>
      <Btn href={IG_URL}>Instagram</Btn>
    </div>

    <div className="fdy-stats">
      {STATS.map(([n, l]) => (
        <div className="fdy-stat" key={l}>
          <b>{n}</b>
          <span>{l}</span>
        </div>
      ))}
    </div>
  </div>
);

/* -------------------------------------------------------------- OVERVIEW --- */

const Meta = ({ label, value }) => (
  <div>
    <p className="fdy-label" style={{ marginBottom: '4px' }}>{label}</p>
    <p className="fdy-meta">{value}</p>
  </div>
);

const Overview = () => (
  <div className="fdy">
    <p className="fdy-p" style={{ maxWidth: '640px', marginBottom: 'clamp(20px, 3vw, 30px)' }}>
      The best business thinking sits inside hour-long interviews nobody has time to watch.
      The Foundry Brief pulls the useful part out and publishes it in the speaker’s own words.
    </p>

    <div style={{
      // one row on every screen, like the other project pages
      display: 'flex', justifyContent: 'space-between',
      border: '1px solid var(--border-color)', borderRadius: '10px',
      background: 'var(--bg-secondary)', padding: 'clamp(16px, 2.4vw, 24px)',
      gap: 'clamp(8px, 2.4vw, 26px)'
    }}>
      <Meta label="Role" value="Founder" />
      <Meta label="Category" value="Business publication" />
      <Meta label="Since" value="2026" />
      <Meta label="Status" value="Live" />
    </div>
  </div>
);

/* ------------------------------------------------------------- LIVE SITE --- */

const LiveSite = () => {
  const [loaded, setLoaded] = useState(false);

  // A cached iframe can finish loading before React attaches onLoad, so the
  // event never fires and the placeholder would sit there for good.
  useEffect(() => {
    const t = setTimeout(() => setLoaded(true), 6000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="fdy">
      <div style={{
        border: '1px solid var(--border-color)', borderRadius: '10px', overflow: 'hidden',
        background: 'var(--bg-secondary)', boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px',
          borderBottom: '1px solid var(--border-color)'
        }}>
          {['#ff5f57', '#febc2e', '#28c840'].map((c) => (
            <span key={c} style={{ width: '10px', height: '10px', borderRadius: '50%', background: c }} />
          ))}
          <div style={{
            flex: 1, textAlign: 'center', fontSize: '12px', color: 'var(--text-secondary)',
            background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
            borderRadius: '6px', padding: '4px 10px', overflow: 'hidden', whiteSpace: 'nowrap'
          }}>
            thefoundrybrief.com
          </div>
          <a href={SITE} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', textDecoration: 'none' }}>
            Open ↗
          </a>
        </div>

        <div style={{ position: 'relative', width: '100%', height: 'clamp(420px, 72vh, 780px)', background: '#0d2340' }}>
          {!loaded && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
              justifyContent: 'center', color: 'rgba(255,255,255,0.5)', fontSize: '13px'
            }}>
              Loading the live site…
            </div>
          )}
          <iframe
            src={SITE}
            title="The Foundry Brief — live site"
            loading="lazy"
            onLoad={() => setLoaded(true)}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none', zIndex: 1 }}
          />
        </div>
      </div>
      <p style={{ fontSize: '13px', textAlign: 'center', marginTop: '12px' }}>
        <a href={SITE} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>
          Open in a new tab ↗
        </a>
      </p>
    </div>
  );
};

/* --------------------------------------------------------------- SCREENS --- */

const Screens = () => (
  <div className="fdy">
    <div className="fdy-grid-2">
      {SCREENS.map(([src, label]) => (
        <figure key={src} style={{ margin: 0 }}>
          <div style={{
            border: '1px solid var(--border-color)', borderRadius: '10px', overflow: 'hidden',
            background: 'var(--bg-secondary)', boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
          }}>
            <img src={src} alt={label} loading="lazy" style={{ width: '100%', display: 'block' }} />
          </div>
          <figcaption className="fdy-label" style={{ marginTop: '8px', textAlign: 'center' }}>{label}</figcaption>
        </figure>
      ))}
    </div>

    <div className="fdy-grid-2" style={{ marginTop: 'clamp(16px, 2.4vw, 24px)', maxWidth: '540px', marginLeft: 'auto', marginRight: 'auto' }}>
      {[['/foundry-screens/home-mobile.webp', 'Front page on mobile'],
        ['/foundry-screens/article-mobile.webp', 'Article on mobile']].map(([src, label]) => (
        <figure key={src} style={{ margin: 0 }}>
          <div style={{
            border: '1px solid var(--border-color)', borderRadius: '10px', overflow: 'hidden',
            background: 'var(--bg-secondary)', boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
          }}>
            <img src={src} alt={label} loading="lazy" style={{ width: '100%', display: 'block' }} />
          </div>
          <figcaption className="fdy-label" style={{ marginTop: '8px', textAlign: 'center' }}>{label}</figcaption>
        </figure>
      ))}
    </div>
  </div>
);

/* --------------------------------------------------------------- LIBRARY --- */

const Library = () => {
  const max = TOPICS[0][1];
  return (
    <div className="fdy fdy-grid-2" style={{ gap: 'clamp(20px, 4vw, 44px)', alignItems: 'start' }}>
      <div>
        <p className="fdy-label" style={{ marginBottom: '14px' }}>Articles per topic</p>
        <div style={{ display: 'grid', gap: '11px' }}>
          {TOPICS.map(([name, count]) => (
            <div key={name}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                <span style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 500 }}>{name}</span>
                <span style={{ fontSize: '13px', color: 'var(--text-secondary)', fontWeight: 600 }}>{count}</span>
              </div>
              <div style={{ height: '5px', background: 'var(--bg-accent)', borderRadius: '999px' }}>
                <div style={{ height: '100%', width: `${(count / max) * 100}%`, background: NAVY, borderRadius: '999px' }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <p className="fdy-label" style={{ marginBottom: '14px' }}>Most covered — 18 of 118 founders</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '7px' }}>
          {FOUNDERS.map((f) => <span className="fdy-chip" key={f}>{f}</span>)}
        </div>
      </div>
    </div>
  );
};

/* -------------------------------------------------------------- ARTICLES --- */

const Articles = () => (
  <div className="fdy fdy-grid-3">
    {ARTICLES.map((a) => (
      <a key={a.id} className="fdy-card" href={`${SITE}/article/${a.slug}/`} target="_blank" rel="noopener noreferrer">
        <img src={`/foundry-articles/${a.id}.webp`} alt={a.person} loading="lazy"
          style={{ width: '100%', aspectRatio: '4/3', objectFit: 'cover', display: 'block' }} />
        <div style={{ padding: 'clamp(12px, 1.6vw, 16px)', display: 'flex', flexDirection: 'column', flex: 1 }}>
          <p className="fdy-label" style={{ marginBottom: '8px' }}>{a.topic}</p>
          <h3 className="fdy-h3" style={{ marginBottom: '10px' }}>{a.headline}</h3>
          <p className="fdy-p" style={{ fontSize: '13px', fontStyle: 'italic', marginBottom: '12px' }}>“{a.quote}”</p>
          <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: 'var(--text-secondary)' }}>
            <span>{a.person}</span>
            <span>{a.read}</span>
          </div>
        </div>
      </a>
    ))}
  </div>
);

/* ------------------------------------------------------------- INSTAGRAM --- */

const Reels = () => (
  <div className="fdy">
    <div className="fdy-reels">
      {REELS.map((p) => (
        <a key={p.code} className="fdy-reel" href={foundryPostUrl(p.code)} target="_blank" rel="noopener noreferrer"
          title={`${p.founder} — ${p.title}`}>
          {p.thumb ? (
            <img src={p.thumb} alt={p.founder} loading="lazy" />
          ) : (
            <div className="ph">
              <b>{p.founder}</b>
              <span className="t">{p.title}</span>
            </div>
          )}
          <span className="m">▶ {fmt(p.views)} · ♥ {fmt(p.likes)}</span>
        </a>
      ))}
    </div>

    <p style={{ fontSize: '12px', marginTop: '14px' }}>
      <a href={IG_URL} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>
        @thefoundrybrief ↗
      </a>
    </p>
  </div>
);

/* ----------------------------------------------------------------- BRAND --- */

const Brand = () => (
  <div className="fdy">
    <div style={{
      border: '1px solid var(--border-color)', borderRadius: '10px',
      overflow: 'hidden', marginBottom: 'clamp(16px, 2.4vw, 24px)'
    }}>
      <img src="/foundry-screens/masthead.webp" alt="The Foundry Brief masthead"
        style={{ width: '100%', display: 'block' }} />
    </div>

    <div className="fdy-grid-2" style={{ gap: 'clamp(20px, 4vw, 44px)', alignItems: 'start' }}>
      <div>
        <p className="fdy-p">
          Navy band, cream page, one warm sand tone. Financier Display for the headlines,
          no rounded corners anywhere.
        </p>
      </div>

      <div className="fdy-grid-2" style={{ gap: '10px' }}>
        {PALETTE.map(([hex, name, note]) => (
          <div key={hex} style={{
            border: '1px solid var(--border-color)', borderRadius: '10px', overflow: 'hidden'
          }}>
            <div style={{ background: hex, height: '64px' }} />
            <div style={{ padding: '10px 12px', background: 'var(--bg-secondary)' }}>
              <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>{name}</p>
              <p style={{ fontSize: '11px', color: 'var(--text-secondary)', margin: '4px 0 0', lineHeight: 1.5 }}>{note}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ---------------------------------------------------------------- READER --- */

const Reader = () => (
  <div className="fdy fdy-grid-3">
    {READER.map((title) => (
      <div key={title} style={{
        border: '1px solid var(--border-color)', borderRadius: '10px',
        background: 'var(--bg-secondary)', padding: 'clamp(14px, 2vw, 20px)'
      }}>
        <h3 className="fdy-h3">{title}</h3>
      </div>
    ))}
  </div>
);

/* --------------------------------------------------------------- CONTENT --- */

export const foundryContent = {
  pageMaxWidth: '1180px',

  breadcrumb: { text: 'Back to Home', url: '/' },

  sections: [
    { id: 'hero', spacing: 'clamp(40px, 6vw, 72px)', blocks: [{ type: 'custom', render: () => <Hero /> }] },

    {
      id: 'overview',
      title: 'Overview',
      spacing: 'clamp(40px, 6vw, 72px)',
      blocks: [{ type: 'custom', render: () => <Overview /> }]
    },

    {
      id: 'live',
      title: 'See it live',
      spacing: 'clamp(40px, 6vw, 72px)',
      blocks: [{ type: 'custom', render: () => <LiveSite /> }]
    },

    {
      id: 'site-pages',
      title: 'Explore the site',
      spacing: 'clamp(40px, 6vw, 72px)',
      blocks: [{ type: 'custom', render: () => <PhoneStack live baseUrl="https://thefoundrybrief.com" imgBase="/portfolio-screens/foundry/site/" pages={[
        { file: 'home', label: 'Home', path: '/' },
        { file: 'founders', label: 'Founders', path: '/founders' },
        { file: 'topics', label: 'Topics', path: '/topics' },
        { file: 'elon-musk', label: 'Elon Musk', path: '/founders/elon-musk' },
        { file: 'naval-ravikant', label: 'Naval Ravikant', path: '/founders/naval-ravikant' },
        { file: 'steve-jobs', label: 'Steve Jobs', path: '/founders/steve-jobs' },
        { file: 'article-chesky', label: 'Brian Chesky', path: '/article/sa-238-brian-chesky-design-for-one-person-until-they-love-it-then-get-the-second-person' },
        { file: 'article-bezos', label: 'Jeff Bezos', path: '/article/sa-445-jeff-bezos-build-your-strategy-around-what-wont-change-in-10-years' }
      ]} /> }]
    },

    {
      id: 'screens',
      title: 'The reading experience',
      spacing: 'clamp(40px, 6vw, 72px)',
      blocks: [
        { type: 'custom', render: () => <Screens /> }
      ]
    },

    {
      id: 'library',
      title: 'What is in the archive',
      spacing: 'clamp(40px, 6vw, 72px)',
      blocks: [{ type: 'custom', render: () => <Library /> }]
    },

    {
      id: 'articles',
      title: 'Six pieces from the archive',
      spacing: 'clamp(40px, 6vw, 72px)',
      blocks: [
        { type: 'custom', render: () => <Articles /> }
      ]
    },

    {
      id: 'instagram',
      title: 'Instagram',
      spacing: 'clamp(40px, 6vw, 72px)',
      blocks: [{ type: 'custom', render: () => <Reels /> }]
    },

    {
      id: 'brand',
      title: 'The look',
      spacing: 'clamp(40px, 6vw, 72px)',
      blocks: [{ type: 'custom', render: () => <Brand /> }]
    },

    {
      id: 'reader',
      title: 'What a reader gets',
      spacing: 'clamp(40px, 6vw, 72px)',
      blocks: [{ type: 'custom', render: () => <Reader /> }]
    }
  ],

  callToAction: {
    brand: { bg: "#0D2340", fg: "#F3F1EF", btnBg: "#F3F1EF", btnFg: "#0D2340" , logo: "/the-foundry-brief-logo.png", logoWidth: 72, logoRadius: 14, font: "'Financier Display', Georgia, 'Times New Roman', serif", titleWeight: 500, titleSpacing: "-0.01em" },
    title: 'Read The Foundry Brief',
    text: '557 articles on strategy, leadership, growth and investing — in the words of the people who did it.',
    button: { text: 'Visit thefoundrybrief.com →', url: SITE, style: 'foundry' }
  }
};
